import { Component, OnDestroy, OnInit, Inject } from '@angular/core';
import { FormBuilder, FormGroup, Validators, AbstractControl } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { Observable, Subject, of } from 'rxjs';
import { catchError, filter, finalize, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import { IFieldConfig } from 'src/app/domain/IFieldConfig';
import { ICreateMerchantRequest } from 'src/app/domain/registration/models/merchants/ICreateMerchantRequest';
import { AbstractCrudCES } from 'src/app/shared/components/crud/abstract-crud-ces';
import { TypeCesEnum } from 'src/app/domain/registration/enums/type-ces.enum';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { formatDateForAPI, formatPhoneForAPI, formatPhoneInternational } from 'src/app/shared/utils/api-formatters';
import { MERCHANT_CONFIG_FIELDS } from '../crud-config';
import { MerchantService } from '../crud-service';

type TipoPessoa = 'pf' | 'pj';

const RGX = {
    CNPJ: /^\d{14}$/,
    CPF: /^\d{11}$/,
    CEP: /^\d{8}$/
} as const;

@Component({
    selector: 'app-merchant-crud',
    templateUrl: './crud-ces.component.html',
    styleUrls: ['./crud-ces.component.sass']
})
export class MerchantCESComponent extends AbstractCrudCES<ICreateMerchantRequest> implements OnInit, OnDestroy {
    override title = 'CADASTRAR COMERCIANTE';
    public override config: IFieldConfig[] = MERCHANT_CONFIG_FIELDS;

    merchantForm: FormGroup;
    private isManualAddressMode = false;
    private destroy$ = new Subject<void>();
    isLoading = false;

    constructor(
        public override fb: FormBuilder,
        private dialogRef: MatDialogRef<MerchantCESComponent>,
        private merchantService: MerchantService,
        private toastService: ToastService,
        @Inject(MAT_DIALOG_DATA) public override data: any
    ) {
        super(fb);
        this.merchantForm = this.buildForm();
    }

    override ngOnInit(): void {
        // Processar dados recebidos do dialog
        if (this.data) {
            this.typeCes = this.data.type;
            this.data = this.data.element;

            // Definir título baseado no tipo
            if (this.typeCes === TypeCesEnum.SHOW) {
                this.title = 'VISUALIZAR COMERCIANTE';
            } else if (this.typeCes === TypeCesEnum.EDIT) {
                this.title = 'EDITAR COMERCIANTE';
            } else {
                this.title = 'CADASTRAR COMERCIANTE';
            }

            if (this.typeCes === TypeCesEnum.SHOW || this.typeCes === TypeCesEnum.EDIT) {
                this.loadMerchantData();
            }
        }

        this.wireUpReactivity();
        this.loadStates();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private loadMerchantData(): void {
        if (this.data?.id) {
            this.isLoading = true;
            this.merchantService.findById(this.data.id).subscribe({
                next: (merchant: any) => {
                    this.populateForm(merchant);
                    this.isLoading = false;
                },
                error: (error: any) => {
                    console.error('Erro ao carregar dados do comerciante:', error);
                    this.toastService.showError('Erro ao carregar dados do comerciante');
                    this.isLoading = false;
                }
            });
        } else if (this.data) {
            // Se já tem os dados do elemento, usar diretamente
            this.populateForm(this.data);
        }
    }

    private populateForm(merchant: any): void {
        if (!merchant) return;
        this.merchantForm.patchValue({
            tipoConta: merchant.accountType || '',
            tipoPessoa: merchant.personType || 'pf',
            numeroCpf: merchant.personType === 'pf' ? merchant.documentNumber : '',
            numeroCnpj: merchant.personType === 'pj' ? merchant.documentNumber : '',
            nomeComerciante: merchant.merchantName || '',
            nomeSocial: merchant.socialName || '',
            dataNascimento: merchant.birthDate || '',
            inscricaoEstadual: merchant.stateRegistration || '',
            inscricaoMunicipal: merchant.municipalRegistration || '',
            email: merchant.email || '',
            chavePix: merchant.pixKey || '',
            celularFinanceiro: merchant.financialPhone || '',
            emailFinanceiro: merchant.financialEmail || '',
            horarioRetirada: merchant.horarioRetirada || '08:00',
            cep: merchant.zipCode || '',
            estado: merchant.state || '',
            cidade: merchant.city || '',
            endereco: merchant.address || '',
            numero: merchant.number || '',
            bairro: merchant.neighborhood || '',
            complemento: merchant.complement || '',
            responsavelNome: merchant.responsibleName || '',
            responsavelCpf: merchant.responsibleCpf || '',
            responsavelRg: merchant.responsibleRg || '',
            responsavelNacionalidade: merchant.responsibleNationality || '',
            responsavelCelular: merchant.responsiblePhone || '',
            responsavelEmail: merchant.responsibleEmail || '',
            responsavelDataNascimento: merchant.responsibleBirthDate || '',
            responsavelCep: merchant.responsibleZipCode || '',
            responsavelEstado: merchant.responsibleState || '',
            responsavelCidade: merchant.responsibleCity || '',
            responsavelEndereco: merchant.responsibleAddress || '',
            responsavelNumero: merchant.responsibleNumber || '',
            responsavelBairro: merchant.responsibleNeighborhood || '',
            responsavelComplemento: merchant.responsibleComplement || ''
        });
    }

    private buildForm(): FormGroup {
        return this.fb.group({
            tipoPessoa: ['pf', [Validators.required]],
            numeroCnpj: ['', []],
            numeroCpf: ['', []],
            nomeComerciante: ['', [Validators.required]],
            nomeSocial: ['', [Validators.required]],
            dataNascimento: ['', [Validators.required]],
            inscricaoEstadual: [''],
            inscricaoMunicipal: [''],
            email: ['', [Validators.required, Validators.email]],

            chavePix: ['', [Validators.required]],
            celularFinanceiro: ['', [Validators.required]],
            emailFinanceiro: ['', [Validators.required, Validators.email]],

            cep: ['', [Validators.required, Validators.pattern(RGX.CEP)]],
            estado: ['', [Validators.required]],
            cidade: ['', [Validators.required]],
            endereco: ['', [Validators.required]],
            numero: ['', [Validators.required]],
            bairro: ['', [Validators.required]],
            complemento: [''],

            responsavelNome: ['', [Validators.required]],
            responsavelCpf: ['', []],             // validado dinamicamente
            responsavelRg: ['', [Validators.required]],
            responsavelNacionalidade: ['Brasileiro', [Validators.required]],
            responsavelCelular: ['', [Validators.required, Validators.pattern(RGX.CPF)]], // ajuste se for DDI
            responsavelEmail: ['', [Validators.required, Validators.email]],
            responsavelDataNascimento: ['', [Validators.required]],

            responsavelCep: ['', [Validators.required, Validators.pattern(RGX.CEP)]],
            responsavelEstado: ['', [Validators.required]],
            responsavelCidade: ['', [Validators.required]],
            responsavelEndereco: ['', [Validators.required]],
            responsavelNumero: ['', [Validators.required]],
            responsavelBairro: ['', [Validators.required]],
            responsavelComplemento: [''],

            idExterno: ['', [Validators.required]],
            tipoConta: ['liquidacao', [Validators.required]],
            validarIp: [false],
            ips: ['']
        });
    }

    private wireUpReactivity(): void {
        this.c('tipoPessoa').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((tipo: TipoPessoa) => this.applyPessoaValidators(tipo));

        this.c('validarIp').valueChanges
            .pipe(takeUntil(this.destroy$))
            .subscribe((validar: boolean) => {
                this.toggleValidators('ips', validar ? [Validators.required] : []);
                if (!validar) this.c('ips').setValue('');
            });
    }

    private applyPessoaValidators(tipo: TipoPessoa): void {
        this.c('numeroCnpj').setValue('');
        this.c('numeroCpf').setValue('');
        this.toggleValidators('numeroCnpj', tipo === 'pj' ? [Validators.required, Validators.pattern(RGX.CNPJ)] : []);
        this.toggleValidators('numeroCpf', tipo === 'pf' ? [Validators.required, Validators.pattern(RGX.CPF)] : []);
        this.toggleValidators('inscricaoEstadual', tipo === 'pj' ? [Validators.required] : []);
    }

    private toggleValidators(controlName: string, validators: any[]): void {
        const ctrl = this.c(controlName);
        ctrl.setValidators(validators);
        ctrl.updateValueAndValidity({ emitEvent: false });
    }

    private loadStates(): void {
        this.merchantService.getStates().pipe(
            take(1),
            map((states: any[]) => states.map(s => s.sigla)),
            tap((stateOptions) => {
                this.setOptions('estado', stateOptions);
                this.setOptions('responsavelEstado', stateOptions);
            }),
            catchError(err => {
                console.error('Erro ao carregar estados:', err);
                return of([]);
            })
        ).subscribe();
    }

    public onChangeState(stateCode: string): void {
        if (!stateCode) return;
        this.c('cidade').reset();
        this.fetchCitiesInto('cidade', stateCode);
    }

    public onChangeResponsavelState(stateCode: string): void {
        if (!stateCode) return;
        this.c('responsavelCidade').reset();
        this.fetchCitiesInto('responsavelCidade', stateCode);
    }

    private fetchCitiesInto(field: 'cidade' | 'responsavelCidade', uf: string): void {
        this.getCitiesByUf(uf).pipe(
            take(1),
            map((cities: any[]) => cities.map(c => c.nome)),
            tap((names) => this.setOptions(field, names)),
            catchError(err => {
                console.error('Erro ao carregar cidades:', err);
                this.setFallbackField(field, field.includes('responsavel') ? 'Cidade do Responsável' : 'Cidade');
                return of([]);
            })
        ).subscribe();
    }

    private setOptions(fieldName: string, options: any[]): void {
        const field = this.getFieldConfig(fieldName);
        if (field) field.options = options;
    }

    private setFallbackField(fieldName: string, label: string): void {
        const field = this.getFieldConfig(fieldName);
        if (field && field.fallbackType) {
            field.type = field.fallbackType;
            field.label = field.fallbackLabel || label;
            field.placeholder = field.fallbackPlaceholder || `Digite ${label.toLowerCase()}`;
            field.options = [];
        }
    }

    public onChangeCep(): void {
        this.handleCepChange('');
    }

    public onChangeResponsavelCep(): void {
        this.handleCepChange('responsavel');
    }

    private handleCepChange(prefix: '' | 'responsavel'): void {
        const control = this.c(this.fName(prefix, 'Cep'));
        const value: string = control.value;
        if (!value || value.length !== 8) return;

        this.merchantService.getCepInfo(value).pipe(
            take(1),
            tap((result: any) => {
                if (this.isManualAddressMode) {
                    this.patchAddress(prefix, result);
                    return;
                }
            }),
            filter(() => !this.isManualAddressMode),
            switchMap((result: any) =>
                this.getCitiesByUf(result.state).pipe(
                    take(1),
                    tap((resp: any[]) => {
                        const cityField = this.fName(prefix, 'Cidade');
                        this.c(cityField).reset();
                        this.setOptions(cityField, resp.map(v => v.nome));
                        this.patchAddress(prefix, result);
                    }),
                    catchError(err => {
                        console.error('Erro ao carregar cidades do CEP:', err);
                        this.setFallbackField(this.fName(prefix, 'Cidade'),
                            prefix ? 'Cidade do Responsável' : 'Cidade');
                        this.patchAddress(prefix, result);
                        return of(null);
                    })
                )
            ),
            catchError(err => {
                console.error('Erro ao consultar CEP:', err);
                this.toastService.showWarn('CEP não encontrado ou erro na consulta.');
                return of(null);
            })
        ).subscribe();
    }

    private patchAddress(prefix: '' | 'responsavel', result: any): void {
        const patch: any = {};
        patch[this.fName(prefix, 'Endereco')] = result.address;
        patch[this.fName(prefix, 'Bairro')] = result.neighborhood;
        patch[this.fName(prefix, 'Cidade')] = result.city;
        patch[this.fName(prefix, 'Estado')] = result.state;
        this.merchantForm.patchValue(patch);
    }

    private fName(prefix: '' | 'responsavel', name: string): string {
        return prefix ? `responsavel${name}` : name.charAt(0).toLowerCase() + name.slice(1);
    }

    public getCitiesByUf(uf: string): Observable<any[]> {
        return this.merchantService.getCitiesByUf(uf);
    }

    onSubmit(): void {
        if (this.merchantForm.invalid) {
            this.markAllAsTouched(this.merchantForm);
            this.toastService.showWarn('Por favor, corrija os erros no formulário antes de continuar.');
            return;
        }

        const formatted = this.formatFormDataForAPI(this.merchantForm.value);
        const request = this.mapToRequest(formatted);

        this.merchantService.createMerchant(request).pipe(
            take(1),
            tap((response) => {
                this.toastService.showSuccess(`Comerciante cadastrado com sucesso! ${response?.data?.nome_fantasia ?? ''}`);
                this.merchantForm.markAsPristine();
                this.dialogRef.close({ saved: true });
            }),
            catchError((error) => {
                this.toastService.showError(this.normalizeApiError(error));
                return of(null);
            }),
            finalize(() => this.isLoading = false)
        ).subscribe();
    }

    private mapToRequest(form: any): ICreateMerchantRequest {
        return {
            geral: {
                tipoPessoa: form.tipoPessoa,
                numeroCnpj: form.tipoPessoa === 'pj' ? form.numeroCnpj : form.numeroCpf,
                nomeComerciante: form.nomeComerciante,
                nomeSocial: form.nomeSocial,
                dataNascimento: form.dataNascimento,
                inscricaoEstadual: form.inscricaoEstadual || null,
                inscricaoMunicipal: form.inscricaoMunicipal || null,
                email: form.email,
                financeiro: {
                    chavePix: form.chavePix,
                    celularFinanceiro: form.celularFinanceiro,
                    emailFinanceiro: form.emailFinanceiro
                },
                endereco: {
                    cep: form.cep,
                    estado: form.estado,
                    cidade: form.cidade,
                    endereco: form.endereco,
                    numero: form.numero,
                    bairro: form.bairro,
                    complemento: form.complemento || null
                }
            },
            responsavel: {
                nome: form.responsavelNome,
                cpf: form.responsavelCpf,
                rg: form.responsavelRg,
                nacionalidade: form.responsavelNacionalidade,
                celular: form.responsavelCelular,
                email: form.responsavelEmail,
                dataNascimento: form.responsavelDataNascimento,
                endereco: {
                    cep: form.responsavelCep,
                    estado: form.responsavelEstado,
                    cidade: form.responsavelCidade,
                    endereco: form.responsavelEndereco,
                    numero: form.responsavelNumero,
                    bairro: form.responsavelBairro,
                    complemento: form.responsavelComplemento || null
                }
            },
            idExterno: form.idExterno,
            tipoConta: form.tipoConta,
            configuracao: {
                validarIp: form.validarIp,
                ip: form.validarIp && form.ips ? form.ips.split(',').map((ip: string) => ip.trim()) : []
            }
        };
    }

    private normalizeApiError(error: any): string {
        if (!error) return 'Erro ao cadastrar comerciante';
        const status = error.status;

        if (status === 400) {
            const msg: string | undefined = error?.error?.message;
            if (msg?.includes('|')) {
                const list = msg.split(', ').map((e: string) => e.split('|')[1]?.trim() || e);
                return `Erros da API Canvi:\n${list.join('\n')}`;
            }
            if (msg) return msg;

            const arr = error?.error?.errors;
            if (Array.isArray(arr)) {
                const list = arr.map((e: string) => e.split('|')[1]?.trim() || e);
                return `Erros de validação:\n${list.join('\n')}`;
            }
            return 'Erro de validação (400).';
        }

        if (status === 409) {
            return error?.error?.message || 'Comerciante já cadastrado com este CNPJ/CPF ou ID externo';
        }

        if (status === 422) return 'Dados inválidos. Verifique os campos e tente novamente.';
        if (status >= 500) return 'Erro interno do servidor. Tente novamente mais tarde.';
        return 'Erro ao cadastrar comerciante';
    }

    private markAllAsTouched(control: AbstractControl): void {
        control.markAllAsTouched();
    }

    private formatFormDataForAPI(formValue: any): any {
        return {
            ...formValue,
            dataNascimento: formatDateForAPI(formValue.dataNascimento),
            responsavelDataNascimento: formatDateForAPI(formValue.responsavelDataNascimento),
            celularFinanceiro: formatPhoneInternational(formValue.celularFinanceiro),
            responsavelCelular: formatPhoneInternational(formValue.responsavelCelular),
            numeroCnpj: formValue.numeroCnpj?.replace(/\D/g, '') || '',
            numeroCpf: formValue.numeroCpf?.replace(/\D/g, '') || '',
            responsavelCpf: formValue.responsavelCpf?.replace(/\D/g, '') || '',
            cep: formValue.cep?.replace(/\D/g, '') || '',
            responsavelCep: formValue.responsavelCep?.replace(/\D/g, '') || ''
        };
    }

    private c(name: string): AbstractControl {
        return this.merchantForm.get(name)!;
    }

    get f() {
        return this.merchantForm.controls;
    }
    get isPessoaJuridica(): boolean {
        return this.c('tipoPessoa')?.value === 'pj';
    }
    get isValidarIp(): boolean {
        return this.c('validarIp')?.value === true;
    }

    public fillWithMockData(): void {
        const mock = {
            tipoPessoa: 'pf', numeroCpf: '12345678901',
            nomeComerciante: 'João Silva Santos', nomeSocial: 'João Silva', dataNascimento: '1990-05-15',
            inscricaoEstadual: '', inscricaoMunicipal: '', email: 'joao.silva@email.com',
            chavePix: 'joao.silva@email.com',
            celularFinanceiro: '11999887766', emailFinanceiro: 'financeiro@joao.com',
            cep: '01310100', estado: 'SP', cidade: 'São Paulo', endereco: 'Avenida Paulista',
            numero: '1000', bairro: 'Bela Vista', complemento: 'Sala 101',
            responsavelNome: 'Maria Silva Santos', responsavelCpf: '98765432100', responsavelRg: '123456789',
            responsavelNacionalidade: 'Brasileiro', responsavelCelular: '11988776655',
            responsavelEmail: 'maria.silva@email.com',
            responsavelDataNascimento: '1985-03-20',
            responsavelCep: '01310100', responsavelEstado: 'SP', responsavelCidade: 'São Paulo',
            responsavelEndereco: 'Avenida Paulista', responsavelNumero: '1000',
            responsavelBairro: 'Bela Vista', responsavelComplemento: 'Apto 201',
            idExterno: 'MOCK' + Date.now(), tipoConta: 'liquidacao', validarIp: false, ips: ''
        };
        this.merchantForm.patchValue(mock);
        this.toastService.showInfo('Formulário preenchido com dados de teste!');
    }
}
